import { type FlagStateData, flagState } from '@mail/flags';
import type { FlagStateUpdateData } from '@mail/flags/dist/flagState';
import type { ValueOf } from '@mail-core/std/type';
import { FLAGS_MAP } from 'reactApp/modules/mailFlags/constants';

type FlagId = keyof typeof FLAGS_MAP;
type FlagIdString = `${FlagId}`;
type FlagKey = ValueOf<typeof FLAGS_MAP>;

function resolveFlagKey(flag: FlagKey | FlagId | FlagIdString): FlagKey {
    if (typeof flag === 'number') {
        return FLAGS_MAP[flag];
    }

    if (flag in FLAGS_MAP) {
        return FLAGS_MAP[flag as unknown as FlagId];
    }

    return flag as FlagKey;
}

export async function isFlag(flagName: FlagKey): Promise<boolean>;
export async function isFlag(flagId: FlagId | FlagIdString): Promise<boolean>;
export async function isFlag(flag: FlagKey | FlagId | FlagIdString): Promise<boolean> {
    const flagKey = resolveFlagKey(flag);

    const { isFlagExist } = flagState<typeof FLAGS_MAP>({
        flagKey,
    });

    return await isFlagExist();
}

export async function getFlag(flagName: FlagKey): Promise<Nullable<FlagStateData>>;
export async function getFlag(flagId: FlagId | FlagIdString): Promise<Nullable<FlagStateData>>;
export async function getFlag(flag: FlagKey | FlagId | FlagIdString): Promise<Nullable<FlagStateData>> {
    const flagKey = resolveFlagKey(flag);

    const { getFlagData } = flagState<typeof FLAGS_MAP>({
        flagKey,
    });

    const flagData = await getFlagData();
    if (!flagData) {
        return null;
    }

    return flagData;
}

export async function removeFlag(flagName: FlagKey): Promise<void>;
export async function removeFlag(flagId: FlagId | FlagIdString): Promise<void>;
export async function removeFlag(flag: FlagKey | FlagId | FlagIdString): Promise<void> {
    const flagKey = resolveFlagKey(flag);

    const { eraseFlagState } = flagState<typeof FLAGS_MAP>({
        flagKey,
    });

    await eraseFlagState();
}

export async function updateFlag(flagName: FlagKey, data: FlagStateUpdateData): Promise<void>;
export async function updateFlag(flagId: FlagId | FlagIdString, data: FlagStateUpdateData): Promise<void>;
export async function updateFlag(flag: FlagKey | FlagId | FlagIdString, data: FlagStateUpdateData): Promise<void> {
    const flagKey = resolveFlagKey(flag);

    const { updateFlag: updateFlagState } = flagState<typeof FLAGS_MAP>({
        flagKey,
    });

    await updateFlagState(data);
}
